/**
 * Subscription Process
 * 
 * Manages subscription status and provides UI for subscription management.
 */

import type { ProcessContext } from '../../systems/process.js';
import { ProcessPriority } from '../../systems/process.js';
import { BaseProcess } from '../../systems/base-process.js';
import { BaseViewHTML } from '../../systems/base-view-html.js';
import { registerProcess } from '../../systems/registry.js';
import { bus } from '../../core/events.js';
import { log } from '../../services/logging/index.js';
import { updateState } from '../../core/state.js';
import { 
  getCurrentSubscription,
  createCheckoutSession,
  createPortalSession,
  createTopUpSession,
  type SubscriptionResponse,
  type SubscriptionPlan
} from '../../services/subscription/api.js';
import { getBalance, getUsageHistory, type BalanceResponse } from '../../services/balance/api.js';
import { getJWTToken } from '../../services/auth/token-storage.js';
import { 
  designTokens, 
  createHeading, 
  createButton,
  createContainer,
  createKeyValueList,
  createScrollableContainer
} from '../../core/design-system.js';

interface SubscriptionState {
  subscription: SubscriptionResponse | null;
  balance: BalanceResponse | null;
  loading: boolean;
  error: string | null;
}

export class SubscriptionProcess extends BaseProcess {
  kind: string = 'subscription';

  metadata = {
    priority: ProcessPriority.NORMAL,
    category: 'utility' as const,
    canStop: true,
    preserveState: true,
    timeSensitive: false
  };

  private state: SubscriptionState = {
    subscription: null,
    balance: null,
    loading: false,
    error: null
  };

  private subscriptionView: SubscriptionView | null = null;

  constructor(id: string) {
    super(id);
  }

  protected onStart(ctx: ProcessContext): void {
    log.debug(`[SubscriptionProcess] Starting subscription process ${this.id}`);
    
    // Listen for subscription.show event
    bus.on('subscription.show', () => {
      this.showSubscription(ctx);
    });
    
    // Check URL for /subscription route
    if (window.location.pathname.includes('/subscription')) {
      this.showSubscription(ctx);
    }
    
    bus.emit('process.started', { processId: this.id, kind: this.kind });
  }

  private showSubscription(ctx: ProcessContext): void {
    if (this.subscriptionView) {
      // Already showing, reload subscription status
      this.loadSubscription();
      return;
    }
    
    // Load subscription status and balance
    this.loadSubscription();
    this.loadBalance();
    
    const view = new SubscriptionView(
      this.id,
      `${this.id}-main`,
      this.state,
      this
    );
    const viewInstance = view.toViewInstance();
    this.subscriptionView = view;
    
    ctx.registerView(viewInstance);
    this.views.set(viewInstance.id, viewInstance);
  }

  async loadSubscription(): Promise<void> {
    this.state.loading = true;
    this.state.error = null;
    
    if (this.subscriptionView) {
      this.subscriptionView.updateState(this.state);
    }
    
    const response = await getCurrentSubscription();
    
    this.state.loading = false;
    
    if (response.success) {
      this.state.subscription = response;
      this.state.error = null;

      // Sync plan to global state
      if (response.data?.plan?.slug) {
        try {
          updateState('user', (user) => {
            if (user) {
              user.plan = response.data!.plan.slug as any;
            }
          });
        } catch (e) {
          // User state might not exist yet or updateState might fail if user is undefined
          log.warn('[SubscriptionProcess] Failed to sync plan to global state', e);
        }
      }
    } else {
      this.state.error = response.error || 'Failed to load subscription';
    }
    
    if (this.subscriptionView) {
      this.subscriptionView.updateState(this.state);
    }
  }

  async loadBalance(): Promise<void> {
    const response = await getBalance();
    
    if (response.success) {
      this.state.balance = response;
    }
    
    if (this.subscriptionView) {
      this.subscriptionView.updateState(this.state);
    }
  }

  async subscribeToPlan(planSlug: 'regular' | 'pro'): Promise<void> {
    const token = getJWTToken();
    if (!token) {
      bus.emit('auth.login.required', {});
      return;
    }

    this.state.loading = true;
    if (this.subscriptionView) {
      this.subscriptionView.updateState(this.state);
    }

    const response = await createCheckoutSession(planSlug);
    
    this.state.loading = false;
    
    if (response.success && response.data?.checkoutUrl) {
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl;
    } else {
      this.state.error = response.error || 'Failed to create checkout session';
      if (this.subscriptionView) {
        this.subscriptionView.updateState(this.state);
      }
    }
  }

  async openCustomerPortal(): Promise<void> {
    const token = getJWTToken();
    if (!token) {
      bus.emit('auth.login.required', {});
      return;
    }

    this.state.loading = true;
    if (this.subscriptionView) {
      this.subscriptionView.updateState(this.state);
    }

    const response = await createPortalSession();
    
    this.state.loading = false;
    
    if (response.success && response.data?.portalUrl) {
      // Redirect to Stripe Customer Portal
      window.location.href = response.data.portalUrl;
    } else {
      this.state.error = response.error || 'Failed to create portal session';
      if (this.subscriptionView) {
        this.subscriptionView.updateState(this.state);
      }
    }
  }

  async showTopUp(): Promise<void> {
    const token = getJWTToken();
    if (!token) {
      bus.emit('auth.login.required', {});
      return;
    }

    // Show top-up options (for now, use a simple prompt)
    // TODO: Create a proper top-up modal/component
    const amountStr = prompt('Enter amount to top up (in dollars, e.g., 10 for $10.00):');
    if (!amountStr) return;

    const amountDollars = parseFloat(amountStr);
    if (isNaN(amountDollars) || amountDollars < 1) {
      alert('Invalid amount. Minimum is $1.00');
      return;
    }

    const amountCents = Math.round(amountDollars * 100);

    this.state.loading = true;
    if (this.subscriptionView) {
      this.subscriptionView.updateState(this.state);
    }

    const response = await createTopUpSession(amountCents);
    
    this.state.loading = false;
    
    if (response.success && response.data?.checkoutUrl) {
      // Redirect to Stripe Checkout
      window.location.href = response.data.checkoutUrl;
    } else {
      this.state.error = response.error || 'Failed to create top-up session';
      if (this.subscriptionView) {
        this.subscriptionView.updateState(this.state);
      }
      alert(this.state.error);
    }
  }
}

/**
 * Subscription View
 * 
 * Displays subscription status and management options.
 */
class SubscriptionView extends BaseViewHTML {
  constructor(
    processId: string,
    viewId: string,
    private state: SubscriptionState,
    private process: SubscriptionProcess
  ) {
    super(processId, viewId, {
      sizes: [1, 3], // Full height, normal width (default); full height, double width (alternative)
      showDismissButton: true,
      resizable: true,
    });
  }

  protected getContentHTML(): string {
    const heading = createHeading(2, 'Subscription');
    
    if (this.state.loading) {
      return createContainer(
        heading + `<div style="padding: ${designTokens.padding.xl}; text-align: center; color: ${designTokens.contentColorMuted};">Loading subscription status...</div>`,
        { padding: 'lg', flexDirection: 'column' }
      );
    }
    
    if (this.state.error) {
      return createContainer(
        heading + `<div style="padding: ${designTokens.padding.md}; color: rgba(255, 0, 0, 0.8);">${this.escapeHtml(this.state.error)}</div>`,
        { padding: 'lg', flexDirection: 'column' }
      );
    }
    
    const subscription = this.state.subscription;
    if (!subscription || !subscription.data) {
      return createContainer(
        heading + `<div style="padding: ${designTokens.padding.xl}; text-align: center; color: ${designTokens.contentColorMuted};">No subscription data available</div>`,
        { padding: 'lg', flexDirection: 'column' }
      );
    }
    
    const plan = subscription.data.plan;
    const sub = subscription.data.subscription;
    
    // Build subscription info
    const subscriptionInfo: Record<string, unknown> = {
      'Plan': plan.name,
      'Price': `€${plan.price.toFixed(2)}/month`,
      'Status': sub?.status || 'active'
    };
    
    if (sub) {
      if (sub.current_period_end) {
        const endDate = new Date(sub.current_period_end);
        subscriptionInfo['Renews'] = endDate.toLocaleDateString();
      }
      if (sub.cancel_at_period_end) {
        subscriptionInfo['Cancels'] = 'At period end';
      }
    }
    
    // Build balance info
    let balanceSection = '';
    if (this.state.balance?.success && this.state.balance.data) {
      const balance = this.state.balance.data;
      // Use availableCents if provided, otherwise fall back to balanceCents
      const availableCents = balance.availableCents ?? balance.balanceCents;
      const reservedCents = balance.reservedCents ?? 0;
      const balanceDollars = (availableCents / 100).toFixed(2);
      const isLow = availableCents < 100; // Less than $1.00 available
      
      balanceSection = `
        <div style="margin-top: ${designTokens.padding.lg}; padding: ${designTokens.padding.md}; background: ${isLow ? 'rgba(255, 200, 0, 0.1)' : 'rgba(0, 200, 0, 0.1)'}; border-radius: ${designTokens.borderRadius.md}; border: 1px solid ${isLow ? 'rgba(255, 200, 0, 0.3)' : 'rgba(0, 200, 0, 0.3)'};">
          <div style="font-weight: 600; margin-bottom: ${designTokens.padding.sm}; color: ${designTokens.contentColor};">Credits</div>
          <div style="font-size: 1.5em; font-weight: 700; color: ${isLow ? 'rgba(255, 150, 0, 1)' : 'rgba(0, 150, 0, 1)'};">
            $${balanceDollars}
          </div>
          <div style="font-size: 0.9em; color: ${designTokens.contentColorMuted}; margin-top: ${designTokens.padding.xs};">
            ${availableCents} credits available${reservedCents > 0 ? ` (${reservedCents} reserved)` : ''}
          </div>
          ${isLow ? `
            <div style="margin-top: ${designTokens.padding.sm}; padding: ${designTokens.padding.sm}; background: rgba(255, 200, 0, 0.2); border-radius: ${designTokens.borderRadius.sm};">
              <div style="font-size: 0.85em; color: rgba(200, 100, 0, 1);">
                ⚠️ Low balance. Consider topping up to continue using Pivot.
              </div>
            </div>
          ` : ''}
          <div style="margin-top: ${designTokens.padding.md};">
            ${createButton('Top Up Credits', {
              className: 'top-up-credits',
              variant: 'primary'
            })}
          </div>
        </div>
      `;
    }
    
    const infoList = createKeyValueList(subscriptionInfo);
    
    // Action buttons
    let actionButtons = '';
    
    if (plan.slug === 'free') {
      actionButtons = `
        <div style="display: flex; gap: ${designTokens.padding.md}; margin-top: ${designTokens.padding.lg};">
          ${createButton('Subscribe to Regular (€20/month)', {
            className: 'subscribe-regular',
            variant: 'primary'
          })}
          ${createButton('Subscribe to Pro (€200/month)', {
            className: 'subscribe-pro',
            variant: 'primary'
          })}
        </div>
      `;
    } else if (plan.slug === 'regular') {
      actionButtons = `
        <div style="display: flex; gap: ${designTokens.padding.md}; margin-top: ${designTokens.padding.lg};">
          ${createButton('Upgrade to Pro (€200/month)', {
            className: 'subscribe-pro',
            variant: 'primary'
          })}
          ${createButton('Manage Subscription', {
            className: 'manage-subscription',
            variant: 'secondary'
          })}
        </div>
      `;
    } else if (plan.slug === 'pro') {
      actionButtons = `
        <div style="display: flex; gap: ${designTokens.padding.md}; margin-top: ${designTokens.padding.lg};">
          ${createButton('Manage Subscription', {
            className: 'manage-subscription',
            variant: 'primary'
          })}
        </div>
      `;
    }
    
    const scrollableContent = createScrollableContainer(
      infoList + balanceSection + actionButtons,
      { direction: 'vertical' }
    );
    
    return createContainer(
      heading + scrollableContent,
      { padding: 'lg', flexDirection: 'column' }
    );
  }

  protected render(): void {
    super.render();
    
    if (!this.element) return;
    
    // Setup button handlers
    const subscribeRegularBtn = this.element.querySelector('.subscribe-regular') as HTMLButtonElement;
    const subscribeProBtn = this.element.querySelector('.subscribe-pro') as HTMLButtonElement;
    const manageBtn = this.element.querySelector('.manage-subscription') as HTMLButtonElement;
    const topUpBtn = this.element.querySelector('.top-up-credits') as HTMLButtonElement;
    
    if (subscribeRegularBtn) {
      subscribeRegularBtn.addEventListener('click', () => {
        this.process.subscribeToPlan('regular');
      });
    }
    
    if (subscribeProBtn) {
      subscribeProBtn.addEventListener('click', () => {
        this.process.subscribeToPlan('pro');
      });
    }
    
    if (manageBtn) {
      manageBtn.addEventListener('click', () => {
        this.process.openCustomerPortal();
      });
    }
    
    if (topUpBtn) {
      topUpBtn.addEventListener('click', () => {
        this.process.showTopUp();
      });
    }
  }

  updateState(newState: SubscriptionState): void {
    this.state = newState;
    this.markDirty();
  }
}

registerProcess('subscription', (id: string) => new SubscriptionProcess(id));

